import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { Shippings } from '@app/pages/checkout-app/types';

export const NO_VALUE_CATEGORY: string | null = null;
export const NO_VALUE_LABEL = 'No value';

interface ParsedMetafieldCondition {
  namespace: string;
  key: string;
  value: string;
}

// Display-condition `name` values, as stored by the conditions editor (see configs.ts).
const METAFIELD_CONDITION_NAME_BY_FIELD: Partial<Record<Checkout.CategoryGroupByFieldType, string>> = {
  variantMetafield: 'variant_metafield',
  productMetafield: 'product_metafield',
};

const CATEGORY_DEFINING_OPERATORS = new Set(['is', 'is_only']);

const parseMetafieldConditionValue = (raw: unknown): ParsedMetafieldCondition | null => {
  if (typeof raw !== 'string' || !raw.includes(':')) {
    return null;
  }
  const separatorIndex = raw.indexOf(':');
  const namespaceKey = raw.slice(0, separatorIndex);
  const value = raw.slice(separatorIndex + 1);
  const dotIndex = namespaceKey.indexOf('.');
  if (dotIndex === -1) {
    return { namespace: '', key: namespaceKey.trim(), value: value.trim() };
  }
  return {
    namespace: namespaceKey.slice(0, dotIndex).trim(),
    key: namespaceKey.slice(dotIndex + 1).trim(),
    value: value.trim(),
  };
};

export const getMethodCategory = (method: Shippings, groupBy: Checkout.CategoryShippingGroupBy): string | null => {
  if (!groupBy) {
    return NO_VALUE_CATEGORY;
  }
  const targetKey = groupBy.metafield?.key?.toLowerCase();
  const targetNamespace = groupBy.metafield?.namespace?.toLowerCase();
  const expectedConditionName = groupBy.field ? METAFIELD_CONDITION_NAME_BY_FIELD[groupBy.field] : undefined;
  const conditions = (method.conditions as Array<{ name?: string; condition?: string; values?: unknown }>) ?? [];

  for (const condition of conditions) {
    if (!expectedConditionName || condition?.name?.toLowerCase() !== expectedConditionName) {
      continue;
    }
    if (!CATEGORY_DEFINING_OPERATORS.has(condition?.condition ?? '')) {
      continue;
    }
    const parsed = parseMetafieldConditionValue(condition.values);
    if (!parsed) {
      continue;
    }
    const keyMatches = !targetKey || parsed.key.toLowerCase() === targetKey;
    const namespaceMatches = !targetNamespace || parsed.namespace.toLowerCase() === targetNamespace;
    if (keyMatches && namespaceMatches && parsed.value) {
      return parsed.value;
    }
  }
  return NO_VALUE_CATEGORY;
};

export interface CategoryGroup {
  category: string | null;
  label: string;
  methods: Shippings[];
}

export const groupMethodsByCategory = (
  methods: Shippings[],
  groupBy: Checkout.CategoryShippingGroupBy,
): CategoryGroup[] => {
  const buckets = new Map<string | null, Shippings[]>();
  for (const method of methods) {
    const category = getMethodCategory(method, groupBy);
    const bucket = buckets.get(category) ?? [];
    bucket.push(method);
    buckets.set(category, bucket);
  }

  const groups: CategoryGroup[] = [];
  // "No value" group is always shown first, per the UX flow.
  if (buckets.has(NO_VALUE_CATEGORY)) {
    groups.push({ category: NO_VALUE_CATEGORY, label: NO_VALUE_LABEL, methods: buckets.get(NO_VALUE_CATEGORY) ?? [] });
    buckets.delete(NO_VALUE_CATEGORY);
  }
  for (const [category, bucketMethods] of buckets) {
    groups.push({ category, label: category ?? NO_VALUE_LABEL, methods: bucketMethods });
  }
  return groups;
};

export const getCategoryFallback = (
  config: Checkout.CategoryBasedShippingConfig | null | undefined,
  category: string | null,
): Checkout.CategoryShippingFallback => {
  const existing = config?.categoryFallbacks?.find((fallback) => (fallback.categoryValue ?? null) === category);
  return existing ?? { categoryValue: category, name: '', rate: 0, rateType: 'FIXED' };
};

export const upsertCategoryFallback = (
  config: Checkout.CategoryBasedShippingConfig,
  fallback: Checkout.CategoryShippingFallback,
): Checkout.CategoryShippingFallback[] => {
  const others = (config.categoryFallbacks ?? []).filter(
    (existing) => (existing.categoryValue ?? null) !== (fallback.categoryValue ?? null),
  );
  return [...others, fallback];
};
